import React, { useEffect, useState, useCallback } from 'react';
import { Modal, Form, Input, InputNumber, Select, Checkbox, notification } from 'antd';
import { PointOfSaleItem } from '../../model/pointofsale/PointOfSaleItem';
import { usePointOfSaleService } from '../../services/pointofsale/PointOfSaleServiceProvider';
import { useCurrencySymbol } from '../../hooks/useFormatters';

const { TextArea } = Input;

interface PosItemFormModalProps {
    open: boolean;
    // The item being edited, or null when adding a new one.
    editingItem: PointOfSaleItem | null;
    // Existing items for the add-mode duplicate-name guard.
    existingItems: PointOfSaleItem[];
    // Called with the saved item after a successful create/update.
    onSaved: (item: PointOfSaleItem) => void;
    onCancel: () => void;
}

// Shared Add/Edit POS-item modal (FORT-315). Extracted from PointOfSale so the promotions screen
// can offer inline POS-item creation too. Owns its own Form and the saveItem call; the parent just
// reacts to onSaved/onCancel.
const PosItemFormModal: React.FC<PosItemFormModalProps> = ({
    open, editingItem, existingItems, onSaved, onCancel,
}) => {
    const posService = usePointOfSaleService();
    const currencySymbol = useCurrencySymbol();
    const [itemForm] = Form.useForm();
    const [itemTypes, setItemTypes] = useState<string[]>([]);
    const [itemTypesLoaded, setItemTypesLoaded] = useState<boolean>(false);

    const loadItemTypes = useCallback(async () => {
        if (itemTypesLoaded) return;
        try {
            const types = await posService.getItemTypes();
            setItemTypes(types);
            setItemTypesLoaded(true);
        } catch (error) {
            console.error('Error fetching item types:', error);
        }
    }, [posService, itemTypesLoaded]);

    // Load the item-type options when the modal opens.
    useEffect(() => {
        if (open) loadItemTypes();
    }, [open, loadItemTypes]);

    // Initial form values, applied synchronously when the form mounts (the Modal uses
    // destroyOnClose, so it remounts on each open). Populating via initialValues rather than an
    // effect avoids a race where a post-paint effect would wipe just-entered input.
    const initialValues = editingItem
        ? {
            name: editingItem.name,
            description: editingItem.description,
            price: editingItem.price,
            type: editingItem.type,
            enabled: editingItem.enabled,
            vat: editingItem.vatApplies ?? true,
        }
        : { name: '', description: '', price: 0.00, type: '', enabled: true, vat: true };

    const handleSave = async () => {
        try {
            const values = await itemForm.validateFields();
            const item: PointOfSaleItem = {
                id: editingItem?.id || '0',
                name: values.name,
                description: values.description || '',
                price: values.price,
                enabled: values.enabled,
                type: values.type,
                vatApplies: values.vat,
                metadata: editingItem?.metadata || {},
            };

            // Duplicate-name guard only when adding a new item.
            if (!editingItem) {
                const existingItem = existingItems.find(i => i.name === item.name);
                if (existingItem) {
                    notification.warning({
                        message: 'Item already exists',
                        description: 'An item with the same name already exists in the system. Please enter a different name.',
                    });
                    return;
                }
            }

            const savedItem = await posService.saveItem(item);
            notification.success({
                message: editingItem ? 'Item Updated' : 'Item Added',
                description: `${savedItem.name} has been ${editingItem ? 'updated' : 'added'} successfully`,
            });
            onSaved(savedItem);
        } catch {
            // validation error handled by form
        }
    };

    return (
        <Modal
            title={editingItem ? 'Edit Item' : 'Add Item'}
            open={open}
            onOk={handleSave}
            onCancel={onCancel}
            okText="Save"
            cancelText="Cancel"
            destroyOnClose
        >
            <Form
                form={itemForm}
                name="posItemForm"
                layout="vertical"
                preserve={false}
                initialValues={initialValues}
            >
                <Form.Item
                    name="name"
                    label="Item Name"
                    rules={[
                        { required: true, message: 'Please enter an item name' },
                        { min: 1, message: 'Item name must be at least 1 character' },
                    ]}
                >
                    <Input placeholder="Enter item name" />
                </Form.Item>

                <Form.Item name="description" label="Description">
                    <TextArea placeholder="Enter item description" rows={3} />
                </Form.Item>

                <Form.Item
                    name="price"
                    label={`Price (${currencySymbol})`}
                    rules={[
                        { required: true, message: 'Please enter a price' },
                        { type: 'number', min: 0, message: 'Price must be 0 or greater' },
                    ]}
                >
                    <InputNumber
                        inputMode="decimal"
                        placeholder="0.00"
                        min={0}
                        step={0.01}
                        precision={2}
                        style={{ width: '100%' }}
                        addonBefore={currencySymbol}
                    />
                </Form.Item>

                <Form.Item
                    name="type"
                    label="Item Type"
                    rules={[{ required: true, message: 'Please select an item type' }]}
                >
                    <Select placeholder="Select item type">
                        {itemTypes.map(type => (
                            <Select.Option key={type} value={type}>{type}</Select.Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item name="enabled" valuePropName="checked">
                    <Checkbox>Enabled</Checkbox>
                </Form.Item>

                <Form.Item name="vat" valuePropName="checked">
                    <Checkbox>VAT</Checkbox>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default PosItemFormModal;
